import {
	DataTexture,
	FloatType,
	LinearFilter,
	LinearSRGBColorSpace,
	Mesh,
	MeshBasicMaterial,
	OrthographicCamera,
	PlaneGeometry,
	RGBAFormat,
	Scene,
	Texture,
	TextureLoader,
	VideoTexture,
	WebGLRenderer,
} from 'three';

import { CopyPass, EffectComposer, RenderPass, ShaderPass, } from "postprocessing";

import {
	ACESMaterial,
	CurveMaterial,
	DitherMaterial,
	FilmGrainMaterial,
	GamutCompressMaterial,
	HueSaturationMaterial,
	LensDistortionMaterial,
	LiftGammaGainMaterial,
	LuminanceSaturationMaterial,
	LUT3DMaterial,
	SaturationSaturationMaterial,
	SubtractiveColourMaterial,
	VibranceMaterial,
	TetrahedralInterpolationMaterial
} from '@/assets/js/materials';

import { TIFFLoader } from '@/assets/js/loaders/TIFFLoader';
import { EXRLoader } from "three/examples/jsm/loaders/EXRLoader";
import { generateUniqueId } from '@/assets/js/Utilities';
import { luts } from '@/assets/js/Boot'

export default class GPU {
	constructor() {
		this.scene = new Scene();
		this.renderer = new WebGLRenderer({
			powerPreference: "high-performance",
			preserveDrawingBuffer: true,
			antialias: false,
			stencil: false,
			depth: false,
		});
		this.renderer.outputColorSpace = LinearSRGBColorSpace;
		// this.renderer.setPixelRatio(window.devicePixelRatio);
		this.renderer.domElement.id = "main-canvas";
		document.querySelector('#canvases').prepend(this.renderer.domElement);

		this.renderer.domElement.addEventListener("webglcontextlost", function(event) {
			event.preventDefault();
			vm.$snackbar(`No WebGL available. Please reload the page and if the error persists, restart the browser.`, "error", {
				class: 'browser-crash-notice',
				timeout: -1,
			});

			setTimeout(() => {
				try {
					gpu.renderer.forceContextRestore();
				} catch(e) {
					//
				}
			}, 1000)
		});

		this.renderer.domElement.addEventListener("webglcontextrestored", function(event) {
			gpu.render();
		})

		this.exportRenderer = new WebGLRenderer({
			powerPreference: "high-performance",
			preserveDrawingBuffer: true,
			depth: false,
			stencil: false,
			antialias: false,
		});
		this.exportRenderer.outputColorSpace = LinearSRGBColorSpace;
		this.exportRenderer.domElement.id = "export-canvas";
		document.querySelector('#canvases').append(this.exportRenderer.domElement);

		this.composer = new EffectComposer(this.renderer, {
			frameBufferType: FloatType,
		});

		this.composer.inputBuffer.name = "InputBuffer";
		this.composer.outputBuffer.name = "OutputBuffer";

		this.geometry = new PlaneGeometry(window.innerWidth, window.innerHeight);
		this.camera = new OrthographicCamera(
			window.innerWidth / -2,
			window.innerWidth / 2,
			window.innerHeight / 2,
			window.innerHeight / -2
		);

		this.THREEtextureLoader = new TextureLoader();
		this.EXRLoader = (new EXRLoader()).setDataType( FloatType );
		this.TIFFLoader = new TIFFLoader();
		this.textureLoader = this.THREEtextureLoader;

		this.canvasRatioX = 1;
		this.canvasRatioY = 1;

		this.thumbnails = {
			scene: new Scene(),
			camera: new OrthographicCamera(window.innerWidth / -2, window.innerWidth / 2, window.innerHeight / 2, window.innerHeight / -2),
		};
	}

	swapTextureLoader() {
		let type = loader.activeMedia.rawDevelopment.type || loader.activeMedia.type;
		if (loader.isEXRFile(type)) this.textureLoader = this.EXRLoader;
		else if (loader.isTiffFile(type)) this.textureLoader = this.TIFFLoader;
		else this.textureLoader = this.THREEtextureLoader;
	}

	setThumbnailRenderer(canvas) {
		this.thumbnails.renderer = new WebGLRenderer({
			preserveDrawingBuffer: true,
			powerPreference: "high-performance",
			canvas: canvas,
			depthTest: false,
			depth: true,
			antialias: false,
		});

		this.thumbnails.renderer.outputColorSpace = LinearSRGBColorSpace;

		this.thumbnails.composer = new EffectComposer(this.thumbnails.renderer);
	}

	getDrawingBufferSize(renderer) {
		return {
			width: renderer.getContext().drawingBufferWidth,
			height: renderer.getContext().drawingBufferHeight,
		};
	}

	zoomCanvas(zoomValue) {
		let currentZoom = this.camera.zoom,
			currentPosX = this.camera.position.x,
			currentPosY = this.camera.position.y,
			leftOffset,
			topOffset,
			newZoom,
			stepsLeft;

		// NOTE do we need this block?
		if (zoomValue < 0 && (currentPosX != 0 || currentPosY != 0)) {
			stepsLeft = parseInt((currentZoom - 1) / zoomValue);
			if (stepsLeft == 0) {
				if (this.camera.zoom > 1) {
					this.camera.position.x = 0;
					this.camera.position.y = 0;
				}
			} else {
				leftOffset = currentPosX / stepsLeft;
				topOffset = currentPosY / stepsLeft;
				this.camera.position.x = currentPosX + leftOffset;
				this.camera.position.y = currentPosY + topOffset;
			}
		}

		newZoom = parseFloat(currentZoom) + parseFloat(zoomValue);

		// Max and min zoom level
		if (newZoom > 3 || newZoom < 1) return;

		this.camera.zoom = newZoom;
		this.camera.updateProjectionMatrix();
		this.composer.render();
	}

	moveScene(newPos) {
		this.camera.position.x = newPos.x;
		this.camera.position.y = newPos.y;
		this.camera.updateProjectionMatrix();
		this.composer.render();
	}

	copyPasses(originComposer, targetComposer) {
		targetComposer.passes = originComposer.passes;
	}

	swapComposerRenderer(renderer) {
		if (renderer == "export") renderer = this.exportRenderer;
		else if (renderer == "thumbnail") renderer = this.thumbnails.renderer;
		// else if (renderer == 'colour-match') renderer = this.colourMatchRenderer;
		else renderer = this.renderer;

		this.composer.setRenderer(renderer);
	}

	createRenderPass(enabled = false) {
		let renderPass = new RenderPass(this.scene, this.camera);
		renderPass.enabled = enabled ? true : false;
		// renderPass.renderToScreen = false;
		renderPass.uniforms = {
			opacity: { value: 1 },
			blendID: { value: 0 },
		};
		renderPass.uid = generateUniqueId();

		return renderPass;
	}

	createCopyPass(enabled = false) {
		const pass = new CopyPass();
		pass.uniforms = {
			opacity: { value: 1 },
			blendID: { value: 0 },
		};
		pass.uid = generateUniqueId();

		return pass;
	}

	createLUTPass(texture) {
		const pass = new ShaderPass(new LUT3DMaterial(texture));
		pass.name = "LUT3DPass";
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.uid = generateUniqueId();

		return pass;
	}

	createACESpass(type, options) {
		const pass = new ShaderPass(new ACESMaterial(type, options));
		pass.name = type;
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.uid = generateUniqueId();

		return pass;
	}

	createDitherPass() {
		let pass = new ShaderPass(new DitherMaterial());
		pass.name = 'DitherPass';
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.uid = generateUniqueId();

		return pass;
	}

	createHueSaturationShaderPass(defines = {}) {
		let pass = new ShaderPass(new HueSaturationMaterial(defines));
		pass.name = "HueSaturationPass";
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.uid = generateUniqueId();
		return pass;
	}

	createSaturationSaturationPass() {
		let pass = new ShaderPass(new SaturationSaturationMaterial());
		pass.name = "SaturationSaturationPass";
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.uid = generateUniqueId();
		return pass;
	}

	createLuminanceSaturationPass() {
		let pass = new ShaderPass(new LuminanceSaturationMaterial());
		pass.name = "LuminanceSaturationPass";
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.uid = generateUniqueId();
		return pass;
	}

	createCurveShaderPass(name, enabled) {
		let texture = this.createCurveTexture();
		let pass = new ShaderPass(new CurveMaterial(texture));
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.enabled = enabled ? true : false;
		pass.name = name ? name : "CurvePass";
		pass.uid = generateUniqueId();

		return pass;
	}

	createSubtractiveColourPass() {
		const pass = new ShaderPass(new SubtractiveColourMaterial());
		pass.name = "SubtractiveColourPass";
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.uid = generateUniqueId();
		return pass;
	}

	createGamutCompressShaderPass() {
		let pass = new ShaderPass(new GamutCompressMaterial());
		pass.name = "GamutCompressPass";
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.enabled = false;
		pass.uid = generateUniqueId();
		return pass;
	}

	createLiftGammaGainPass() {
		const pass = new ShaderPass(new LiftGammaGainMaterial());
		pass.name = "LiftGammaGainPass";
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.uid = generateUniqueId();

		return pass;
	}

	createLensDistortionPass() {
		const pass = new ShaderPass(new LensDistortionMaterial());
		pass.name = "LensDistortionPass";
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.defines = pass.fullscreenMaterial.defines;
		pass.uid = generateUniqueId();

		return pass;
	}

	createFilmGrainPass() {
		const pass = new ShaderPass(new FilmGrainMaterial());
		pass.name = "FilmGrainPass";
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.defines = pass.fullscreenMaterial.defines;
		pass.uid = generateUniqueId();

		return pass;
	}

	createVibrancePass() {
		const pass = new ShaderPass(new VibranceMaterial());
		pass.name = "VibrancePass";
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.uid = generateUniqueId();

		return pass;
	}

	createTetrahedralInterpolationShaderPass() {
		let pass = new ShaderPass(new TetrahedralInterpolationMaterial());
		pass.name = "TetrahedralInterpolationPass";
		pass.uniforms = pass.fullscreenMaterial.uniforms;
		pass.uid = generateUniqueId();

		return pass;
	}

	addPassToComposer(pass, toMiddle = false) {
		if (toMiddle) this.composer.addPass(pass, this.getLastDynamicPassIndex())
		else this.composer.addPass(pass);
	}

	addInitialIDTpass(IDT) {
		let passesList = [];

		let shaderPassIDT = this.createIDTPass(IDT);
		this.addPassToComposer(shaderPassIDT);
		passesList.push(shaderPassIDT);

		let fixBlueLightPass = this.createFixBlueLightPass();
		this.addPassToComposer(fixBlueLightPass);
		passesList.push(fixBlueLightPass);

		let gamutCompressPass = this.createGamutCompressShaderPass();
		this.addPassToComposer(gamutCompressPass);
		passesList.push(gamutCompressPass);

		blocks.addFixed({
			passes: passesList,
			name: "IDT",
			values: IDT,
			gamutCompress: false,
			fixBlueLightArtifacts: false
		});

		this.render();
	}

	addInitialODTpass(ODT) {
		let shaderPassODT = this.createODTPass(ODT);
		this.composer.addPass(shaderPassODT);
		blocks.addFixed({ passes: [shaderPassODT], name: "ODT", values: ODT });
		this.render();
	}

	createFixBlueLightPass() {
		let IDTvalues = luts.IDT.find(idt => idt.value == "IDT100_ACES");
		const pass = this.createACESpass("IDT", { ...IDTvalues, ACES_ODT: 'ODT400', ACES_LMT: true });
		pass.name = 'FixBlueLightPass';
		pass.enabled = false;
		return pass;
	}

	createIDTPass(IDT) {
		let pass;

		if (IDT.type === "LUT") {
			let LUT = luts.get(IDT.name, "IDTluts");
			pass = gpu.createLUTPass(LUT.texture);
		} else {
			pass = this.createACESpass("IDT", { ...IDT, ACES_LMT: lutify.fixBlueLightArtifacts });
		}

		pass.name = "IDTPass";
		pass.title = IDT.name;
		return pass;
	}

	createODTPass(ODT) {
		let pass;

		if (ODT.type === "LUT") {
			let LUT = luts.get(ODT.name, "ODTluts");
			pass = gpu.createLUTPass(LUT.texture);
		} else {
			pass = this.createACESpass("ODT", ODT);
		}

		pass.name = "ODTPass";
		pass.title = ODT.name;
		return pass;
	}

	updateIDT(IDT, showNotification = true) {
		let shaderPassIDT = this.createIDTPass(IDT);

		if (vm.rawDevelopment.updating) shaderPassIDT.enabled = false;

		gpu.composer.passes[0].renderToScreen = false;

		this.composer.removePass(this.composer.passes[1]);
		this.composer.addPass(shaderPassIDT, 1);

		lutify.blocks[1].passes[0] = shaderPassIDT;
		lutify.blocks[1].values = IDT;
		lutify.blocks[1].fixBlueLightArtifacts = lutify.fixBlueLightArtifacts;
		lutify.blocks[1].gamutCompress = lutify.gamutCompress;
		lutify.selectedIDT = IDT;
		vm.$store.dispatch("App/setActiveIDT", _.cloneDeep(IDT));

		if (lutify.fixBlueLightArtifacts && (IDT.type == 'LUT' || IDT.value == "IDT100")) {
			this.composer.passes[2].enabled = true;
		} else this.composer.passes[2].enabled = false;

		if (lutify.gamutCompress) {
			this.composer.passes[3].enabled = true;
		}

		if (!vm.rawDevelopment.updating) this.render();

		if (IDT.value == "IDT100") {
			if (lutify.selectedODT?.value == "ODT400" && lutify.gamutCompress) {
				vm.$nextTick(() => {
					vm.$root.$refs.gamutCompressButton.$el.click();
				})
			}
			if (showNotification) UI.showColorWorkflowNotification();
		}

		if (UI.getActiveBlock().hasClass("preview-based")) {
			this.updatePreviewsForCurrentBlock();
		}
	}

	updatePreviewsForCurrentBlock() {
		lutPreviewer.cloneMainComposerPasses();

		if (UI.getActiveBlock().hasClass("magic-mode-block")) {
			let stepIndex = lutify.magicMode.activeStep;
			lutPreviewer.addPass(_Adjustments.magicMode.controls[stepIndex].name, _Adjustments.magicMode.controls[stepIndex].category, stepIndex);
		}
		if (UI.getActiveBlock().hasClass("ai-magic-mode-block")) {
			let stepIndex = lutify.AIMagicMode.activeStep;
			if (_Adjustments.AIMagicMode.cotrols[stepIndex].name == 'AIControl') {
				_Adjustments.AIMagicMode.addPassForAIPredictedPreview(lutify.getActiveBlock().predictions[stepIndex], stepIndex);
			} else {
				lutPreviewer.addPass(_Adjustments.AIMagicMode.controls[stepIndex].name, _Adjustments.AIMagicMode.controls[stepIndex].category, stepIndex);
			}
		}
		else if (UI.getActiveBlock().hasClass("ai-exposure-block")) {
			_Adjustments.AIExposure.addPassForAIPredictedPreview(lutify.getActiveBlock().predictions, 0);
		}
		else if (UI.getActiveBlock().hasClass("ai-contrast-block")) {
			_Adjustments.AIContrast.addPassForAIPredictedPreview(lutify.getActiveBlock().predictions, 0);
		}
		else if (UI.getActiveBlock().hasClass("ai-white-balance-block")) {
			_Adjustments.AIWhiteBalance.addPassForAIPredictedPreview(lutify.getActiveBlock().predictions, 0);
		}
		else if (UI.getActiveBlock().hasClass("colour-match-block")) {
			lutPreviewer.addPass('LUTcontrols', `ColourMatch-${lutify.getActiveBlock().passes[0].uid}`, undefined, lutify.getActiveBlock().blendingMode);
		}
		else if (UI.getActiveBlock().hasClass("colour-match-v2-block")) {
			lutPreviewer.addPass('LUTcontrols', `ColourMatch-${lutify.getActiveBlock().passes[0].uid}`, undefined, lutify.getActiveBlock().blendingMode);
		}
		else if (UI.getActiveBlock().hasClass("ai-colour-extract-block")) {
			lutPreviewer.addPass('LUTcontrols', `AIColourExtract-${lutify.getActiveBlock().passes[2].uid}`, 2, lutify.getActiveBlock().blendingMode, 'AIColourExtract');
			_Adjustments.AIColourExtract.showGarmutPreview();
		}
		else if (UI.getActiveBlock().hasClass("ai-colour-extract-v2-block")) {
			lutPreviewer.addPass('LUTcontrols', `AIColourExtract-${lutify.getActiveBlock().passes[2].uid}`, 2, lutify.getActiveBlock().blendingMode, 'AIColourExtract');
		}
		else if (UI.getActiveBlock().hasClass("neural-tone-ai-block") ||
			UI.getActiveBlock().hasClass("neural-film-ai-block")
		) {
			// lutPreviewer.addPass('LUTcontrols', 'NeuralToneAI', 2, lutify.getActiveBlock().blendingMode, 'NeuralToneAI');
		}
		else {
			lutPreviewer.addPass(lutify.getActiveBlock().control, lutify.getActiveBlock().name);
		}

		lutPreviewer.render();
	}

	updateODT(ODT, showNotification = true) {
		let shaderPassODT = this.createODTPass(ODT);
		if (vm.rawDevelopment.updating) shaderPassODT.enabled = false;

		gpu.composer.passes[0].renderToScreen = false;

		let index = this.getLastDynamicPassIndex();
		this.composer.removePass(this.composer.passes[index]);
		this.composer.addPass(shaderPassODT, index);

		let ODTblockNum = blocks.getDynamicEndIndex();
		lutify.blocks[ODTblockNum].passes[0] = shaderPassODT;
		lutify.blocks[ODTblockNum].values = ODT;
		lutify.selectedODT = ODT;

		if (!vm.rawDevelopment.updating) this.render();

		if (ODT.value == "ODT400") {
			if (lutify.selectedIDT?.value == "IDT100" && lutify.gamutCompress) {
				vm.$nextTick(() => {
					vm.$root.$refs.gamutCompressButton.$el.click();
				})
			}
			if (showNotification) UI.showColorWorkflowNotification();
		}

		if (UI.getActiveBlock().hasClass("preview-based")) {
			this.updatePreviewsForCurrentBlock();
		}
	}

	// addDitheringPass(renderToScreen = true) {
	// 	let ditheringPass = this.createDitheringPass(renderToScreen);
	// 	this.addPassToComposer(ditheringPass);
	// 	blocks.addFixed({ passes: ditheringPass, name: "DITHERING BLOCK" })
	// 	this.render();
	// }

	// addIdentityPass() {
	// 	let identityPass = this.createIdentityPass();
	// 	this.addPassToComposer(identityPass);
	// 	blocks.addFixed({ pass: identityPass, name: "IDENTITY BLOCK" })
	// 	this.render();
	// }

	getLastDynamicPassIndex() {
		return this.composer.passes.length - config.endFixedPasses;
	}

	getLastBlockPass() {
		return this.composer.passes[this.composer.passes.length - 1 - config.endFixedPasses];
	}

	getLastPass() {
		return this.composer.passes[this.composer.passes.length - 1];
	}

	setLastPass_renderToScreen(option = false) {
		this.composer.passes[this.composer.passes.length - 1].renderToScreen = option;
	}

	disableAllCreativePasses(render = true) {
		if (gpu.composer) {
			gpu.composer.passes.forEach((pass, key) => {
				if (key == 0) {
					pass.renderToScreen = true;
					return;
				}
				pass.enabled = false;
			})

			if (render) gpu.render();
		}
	}

	render() {
		this.composer.render();
		if (vm.$store.state.Scopes.scopesEnabled) {
			if (vm.$store.state.Scopes.activeScopeType.value == "histogram") histogram.render();
			else scopes.update(vm.$store.state.Scopes.activeScopeType.value);
		}
	}

	removePass(uid) {
		this.composer.passes.forEach((pass, i) => {
			if (pass.uid == uid) this.composer.passes.splice(i, 1);
		});
	}

	getPassIndexById(uid) {
		let passIndex = null;
		this.composer.passes.forEach((pass, i) => {
			if (pass.uid == uid) passIndex = i;
		});
		return passIndex;
	}

	loadVideoTexture() {
		return new Promise(resolve => {
			let videoEl = document.createElement("video");
			videoEl.setAttribute("id", "video");
			videoEl.classList.add("hide");
			let interval;

			// because we wait for width and height of video to be fully loaded
			videoEl.addEventListener(
				"loadedmetadata",
				() => {
					let video = document.getElementById("video");

					vm.$store.dispatch("UI/setVideoLoaded", true);
					vm.$store.dispatch("UI/setVideoDuration", video.duration);

					let texture = new VideoTexture(video);
					texture.image.width = texture.image.videoWidth;
					texture.image.height = texture.image.videoHeight;

					// vm.$store.dispatch("Helpers/revokeObjectURL", loader.activeVideo);

					resolve(texture);
				},
				false
			);

			videoEl.addEventListener("timeupdate", e => {
				vm.$store.dispatch("UI/setVideoCurrentTime", e.target.currentTime);
			});

			videoEl.addEventListener("ended", e => {
				vm.$store.dispatch("UI/setVideoPlaying");
			});

			videoEl.addEventListener("seeked", e => {
				gpu.render();
			});

			videoEl.addEventListener("playing", e => {
				if (!interval) {
					interval = setInterval(() => {
						gpu.render();
					}, 40);
				}
			});

			videoEl.addEventListener("pause", e => {
				clearInterval(interval);
				interval = undefined;
			});

			let sourceEl = document.createElement('source');
			sourceEl.type = 'video/mp4';
			videoEl.append(sourceEl);

			// videoEl.addEventListener("ended", e => {
			// 	clearInterval(interval);
			// 	console.log("interval cleard");
			// });

			// videoEl.addEventListener("abort", e => {
			// 	clearInterval(interval);
			// 	console.log("interval cleard");
			// });

			// let videoSource = document.getElementById("video-source");
			UI.container.mainCanvases.append(videoEl);
			sourceEl.src = loader.activeMedia.url;
		})
	}

	getActiveMediaTexture() {
		return new Promise(resolve => {
			if (loader.typeOfActiveMedia('video')) {
				resolve(this.loadVideoTexture());
			}

			if (loader.activeMedia.rawDevelopment.imageData) {
				let texture = new Texture(loader.activeMedia.rawDevelopment.imageData);
				resolve(texture);
			}

			this.swapTextureLoader();

			let activeMediaURL = loader.activeMedia.rawDevelopment.url || loader.activeMedia.url;
			this.textureLoader.load(activeMediaURL, texture => {
				resolve(texture);
			});
		});
	}

	async initThreejs() {
		loader.activeMedia.THREE.texture = await this.getActiveMediaTexture();
		let textureLoaded = loader.activeMedia.THREE.texture;

		lutify.setImageData(textureLoaded.image.width, textureLoaded.image.height);

		lutify.checkImageSize();
		UI.canvas.setSizeCSS();

		this.renderer.setSize(lutify.imageWidth, lutify.imageHeight, false);
		this.composer.setSize(lutify.imageWidth, lutify.imageHeight);

		this.canvasRatioX = window.innerWidth / UI.canvas.$el.width();
		this.canvasRatioY = window.innerHeight / UI.canvas.$el.height();

		// NOTE do we need to set the properties of texture?
		this.texture = textureLoaded;
		this.texture.generateMipmaps = false;
		this.texture.magFilter = LinearFilter;
		this.texture.minFilter = LinearFilter;
		this.texture.needsUpdate = true;

		// let type = loader.activeMedia.rawDevelopment.type || loader.activeMedia.type;
		// if (!loader.isTiffFile(type)) this.texture.type = FloatType;

		this.mesh = new Mesh(
			this.geometry,
			new MeshBasicMaterial({
				map: this.texture
			})
		);

		this.thumbnails.mesh = new Mesh(
			this.geometry,
			new MeshBasicMaterial({
				map: this.texture
			})
		);

		this.mesh.position.y = -0;
		this.mesh.position.z = -300;
		this.thumbnails.mesh.position.y = -0;
		this.thumbnails.mesh.position.z = -300;

		// SCENE
		this.scene.add(this.mesh);
		this.thumbnails.scene.add(this.thumbnails.mesh);

		// PASSES
		this.addInitialPasses();

		// PREVIEWS
		lutPreviewer.init();

		this.render();
		UI.canvas.$el.css("opacity", 1);
		// $(".loader").fadeOut();

		this.setExportRendererSize();

		scopes.init();
		histogram.setSourceCtx(this.renderer.getContext());
		histogram.updateGradients();
	}

	async initNewImage() {
		loader.activeMedia.THREE.texture = await this.getActiveMediaTexture();
		let textureLoaded = loader.activeMedia.THREE.texture;

		if (!textureLoaded) return // when switching raw files fast
		lutify.setImageData(textureLoaded.image.width, textureLoaded.image.height);
		lutify.checkImageSize();
		UI.canvas.setSizeCSS();
		this.renderer.setSize(lutify.imageWidth, lutify.imageHeight, false);
		this.composer.setSize(lutify.imageWidth, lutify.imageHeight);

		this.canvasRatioX = window.innerWidth / UI.canvas.$el.width();
		this.canvasRatioY = window.innerHeight / UI.canvas.$el.height();

		this.texture = textureLoaded;
		this.texture.generateMipmaps = false;
		this.texture.magFilter = LinearFilter;
		this.texture.minFilter = LinearFilter;
		this.texture.needsUpdate = true;

		// let type = loader.activeMedia.rawDevelopment.type || loader.activeMedia.type;
		// if (!loader.isTiffFile(type)) this.texture.type = FloatType;

		this.camera.zoom = 1;
		this.camera.updateProjectionMatrix();

		this.scene.children[0].material.map = this.texture;
		this.moveScene({ x: 0, y: 0 });
		this.render();

		lutPreviewer.setSize();
		UI.lutPreviewThumbnail.setSize(lutPreviewer.width, lutPreviewer.height);

		this.setExportRendererSize()

		if (lutify.splitViewActive) lutify.removeSplitView();

		histogram.setSourceCtx(this.renderer.getContext()); // NOTE maybe no need fot this line
		histogram.updateGradients();
	}

	setExportRendererSize() {
		let w = lutify.imageWidth;
		let h = lutify.imageHeight;

		if (lutify.canSaveFullSizeImage()) {
			let originalWidth = lutify.originalImageSize.width;
			let originalHeight = lutify.originalImageSize.height;
			UI.exportCanvas.setSize(originalWidth, originalHeight);

			if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
				let maxTextureSize = this.exportRenderer.capabilities.maxTextureSize;
				if (originalWidth > maxTextureSize || originalHeight > maxTextureSize) {
					let ratio = originalWidth / originalHeight;

					if (originalWidth > originalHeight) {
						UI.exportCanvas.setSize(maxTextureSize, maxTextureSize / ratio);
					} else {
						UI.exportCanvas.setSize(maxTextureSize * ratio, maxTextureSize);
					}
				}
			}

			let drawingBufferSize = this.getDrawingBufferSize(this.exportRenderer);
			let ratio = drawingBufferSize.width / drawingBufferSize.height;

			if (drawingBufferSize.width > drawingBufferSize.height) {
				w = drawingBufferSize.width;
				h = w / ratio;
			} else {
				h = drawingBufferSize.height;
				w = h * ratio
			}
		}

		this.exportRenderer.setSize(w, h);
	}

	getTexture(objectURL) {
		return new Promise(resolve => {
			this.textureLoader.load(objectURL, textureLoaded => {
				vm.$store.dispatch("Helpers/revokeObjectURL", objectURL);

				this.thumbnails.texture = textureLoaded;
				this.thumbnails.texture.generateMipmaps = false;
				this.thumbnails.texture.magFilter = LinearFilter;
				this.thumbnails.texture.minFilter = LinearFilter;
				this.thumbnails.texture.type = FloatType;
				this.thumbnails.texture.needsUpdate = true;
				resolve(this.thumbnails.texture);
			});
		});
	}

	// async renderThumbnailImage(thumbnailImg, saveLastTexture = true) {
	// 	window.UI.thumbnailCanvas.setSize(thumbnailImg.width, thumbnailImg.height);
	// 	gpu.setThumbnailRenderer(UI.thumbnailCanvas.el);

	// 	let drawingBufferSize = gpu.getDrawingBufferSize(gpu.thumbnails.renderer);
	// 	gpu.thumbnails.renderer.setSize(drawingBufferSize.width, drawingBufferSize.height, false);

	// 	if (saveLastTexture) this.lastTexture = this.scene.children[0].material.map;
	// 	let newTexture = await this.getTexture(thumbnailImg);
	// 	this.scene.children[0].material.map = newTexture;

	// 	this.swapComposerRenderer("thumbnail");
	// 	this.render();
	// }

	async renderThumbnailImageOffCanvas(blob, width, height, IDT, ODT) {
		window.UI.thumbnailCanvas.setSize(width, height);
		gpu.setThumbnailRenderer(UI.thumbnailCanvas.el);

		let drawingBufferSize = gpu.getDrawingBufferSize(gpu.thumbnails.renderer);
		gpu.thumbnails.renderer.setSize(drawingBufferSize.width, drawingBufferSize.height, false);

		gpu.thumbnails.renderer.autoClear = false;
		gpu.thumbnails.renderer.setClearColor(0xffffff);

		let objectURL = URL.createObjectURL(blob);
		let newTexture = await this.getTexture(objectURL);
		gpu.thumbnails.scene.children[0].material.map = this.thumbnails.texture;
		gpu.thumbnails.composer.addPass(new RenderPass(this.thumbnails.scene, this.thumbnails.camera));
		gpu.thumbnails.composer.addPass(gpu.createIDTPass(IDT));
		gpu.thumbnails.composer.addPass(gpu.createODTPass(ODT));

		gpu.thumbnails.camera.updateProjectionMatrix();
		gpu.thumbnails.composer.render();
	}

	resetCamera() {
		this.camera.zoom = 1;
		this.camera.updateProjectionMatrix();
		this.moveScene({ x: 0, y: 0 });
	}

	addInitialPasses() {
		// RENDER PASS
		let renderPass = this.createRenderPass("enabled");
		this.addPassToComposer(renderPass);
		blocks.addFixed({ passes: [renderPass], name: "RENDER BLOCK" });

		// IDT PASS
		this.addInitialIDTpass({ value: "IDT100", name: "Bypass", label: "No Input Transform", initial: true });

		// ODT PASS
		this.addInitialODTpass({ value: "ODT400", name: "Bypass", label: "No Output Transform", initial: true });
	}

	disableDynamicPasses() {
		lutify.blocks.forEach(block => {
			if (block.type == "dynamic") {
				block.passes.forEach(pass => (pass.enabled = false));
			}
		});
	}

	enableDynamicPasses() {
		lutify.blocks.forEach(block => {
			if (block.type == "dynamic") {
				block.passes.forEach(pass => (pass.enabled = true));
			}
		});
	}

	// Turns the changes off
	hideDynamicPasses() {
		let newPasses = [];

		lutify.blocks.forEach(block => {
			if (block.type == "fixed") {
				block.passes.forEach(pass => newPasses.push(pass));
			}
		});

		lutify.tempPasses = this.composer.passes;
		this.composer.passes = newPasses;
	}

	getFixedPasses() {
		return lutify.blocks.filter(block => block.type == "fixed").map(block => block.passes[0]);
	}

	getOriginalImageSrc() {
		this.hideDynamicPasses();
		this.render();
		let originalImage = UI.canvas.$el[0].toDataURL();

		this.composer.passes = lutify.tempPasses;
		this.render();

		delete lutify.tempPasses;
		return originalImage;
	}

	getThumbnailsCanvasSrc() {
		return UI.thumbnailCanvas.el.toDataURL();
	}

	pointsToCurve(points) {
		var array = [];
		for (var i = 0; i < 1024; i++) {
			array.splice(array.length, 0, points[i], points[i], points[i], 1);
			// array.splice(array.length, 0, red[i], green[i], blue[i], 255);
		}

		// var finalArray = new Uint8Array(array);
		var finalArray = new Float32Array(array);
		var curveTexture = new DataTexture(finalArray, 1024, 1);

		curveTexture.format = RGBAFormat;
		curveTexture.type = FloatType;
		curveTexture.needsUpdate = true;
		curveTexture.flipY = true;

		return curveTexture;
	}

	RGBPointsToCurve(pointsRed, pointsGreen, pointsBlue) {
		var array = [];

		for (let i = 0; i < 1024; i++) {
			array.splice(array.length, 0, pointsRed[i], pointsGreen[i], pointsBlue[i], 1);
		}

		// let finalArray = new Uint8Array(array);
		let finalArray = new Float32Array(array);
		let curveTexture = new DataTexture(finalArray, 1024, 1);

		curveTexture.format = RGBAFormat;
		curveTexture.type = FloatType;
		curveTexture.needsUpdate = true;
		curveTexture.flipY = true;

		return curveTexture;
	}

	createCurveTexture() {
		let array = [];
		for (let i = 0; i < 1024; i++) {
			// array.splice(array.length, 0, i, i, i, 255);
			array.splice(array.length, 0, i / 1023, i / 1023, i / 1023, 1);
		}

		// let startArray = new Uint8Array(array);
		let startArray = new Float32Array(array);
		let curveTexture = new DataTexture(startArray, 1024, 1);
		curveTexture.format = RGBAFormat;
		curveTexture.type = FloatType;
		curveTexture.needsUpdate = true;
		curveTexture.flipY = true;

		return curveTexture;
	}

	async getExportData(lutSize, exportType = false, data = false) {
		let lutIdentity = await luts.load([ luts[`identity${lutSize}`] ]);
		lutIdentity = lutIdentity[0].texture.toDataTexture();
		let	w = lutIdentity.image.width;
		let h = lutIdentity.image.height;
		let origTexture = gpu.scene.children[0].material.map;

		// scene.children[1] is the mesh, which contains the actual image rendered on canvas.
		// We are now replacing this material texture with new texture, which is the image of identity LUT.
		// We then render the identity LUT image to the canvas, and all changes (as passes) are applied to it.
		// After that, we will read each pixels color values and store them into a buffer which will then be used as values in the exported file.
		// At the end we will re-asign the value of scene.children[0] to it's original texture, and re-render again
		gpu.scene.children[0].material.map = lutIdentity;
		gpu.scene.children[0].scale.x = w / gpu.scene.children[0].geometry.parameters.width;
		gpu.scene.children[0].scale.y = h / gpu.scene.children[0].geometry.parameters.height;

		let cameraZoom = gpu.camera.zoom;
		let cameraPositionX = gpu.camera.position.x;
		let cameraPositionY = gpu.camera.position.y;
		gpu.camera.zoom = 1;
		gpu.camera.position.x = 0;
		gpu.camera.position.y = 0;

		gpu.camera.left = w / -2;
		gpu.camera.right = w / 2;
		gpu.camera.top = h / 2;
		gpu.camera.bottom = h / -2;
		gpu.camera.updateProjectionMatrix();

		// Saving the values - width, height - of the original texture image
		let tw = gpu.composer.inputBuffer.width,
			th = gpu.composer.inputBuffer.height;

		// gpu.renderer.setPixelRatio(1);
		gpu.renderer.setSize(w, h, false);
		gpu.composer.setSize(w, h);

		// gpu.composer.passes[0].clear = false;
		let originalPasses = gpu.composer.passes;
		let newPasses = [...originalPasses];

		gpu.renderer.clear();

		let blocksData = _.cloneDeep(lutify.getRawBlocksData());

		// filtering out texture block and passes
		blocksData = blocksData.filter(block => block.name !== "midtoneTexture");
		newPasses = newPasses.filter(pass => pass.name !== "DitherPass");

		// filtering out lens distortion block and passes
		blocksData = blocksData.filter(block => block.name !== "LensDistortionEffect");
		newPasses = newPasses.filter(pass => pass.name !== "LensDistortionPass");
		newPasses = newPasses.filter(pass => pass.name !== "FilmGrainPass");

		let isFixBlueLightPassEnabled = newPasses[2].enabled;
		let isGamutCompressPassEnabled = newPasses[3].enabled;

		if (exportType == "ACES") {
			if (data.options.ACESinput && data.options.ACESoutput) {
				// console.log("[EXPORT LUT] ACES input + blocks + ACES output");

				if (data.options.xmp) {
					let LUT = luts.get('sRGB-IDT-33', "IDTluts");
					newPasses[1] = gpu.createLUTPass(LUT.texture); // swapping IDT pass

					if (!lutify.fixBlueLightArtifacts) newPasses[3].enabled = true;

					let sRGB_ODT = luts.ODT.find(odt => odt.value == "ODT410");
					newPasses[newPasses.length - 1] = this.createACESpass("ODT", sRGB_ODT); // swapping ODT pass
				}

				gpu.composer.passes = newPasses;
				gpu.render();

				blocksData = blocksData.splice(1); // render block
			} else if (!data.options.ACESinput && data.options.ACESoutput) {
				// console.log("[EXPORT LUT] blocks + ACES output");

				if (lutify.fixBlueLightArtifacts) newPasses[2].enabled = true;

				let renderPass = newPasses[0]; // extracting render pass
				newPasses = newPasses.splice(config.startFixedPasses); // splicing start fixed passes (also render pass)
				newPasses.splice(0, 0, renderPass); // adding the renderPass to beginning
				gpu.composer.passes = newPasses;
				gpu.render();

				blocksData = blocksData.splice(config.startFixedPasses); // render + IDT block
			} else if (data.options.ACESinput && !data.options.ACESoutput) {
				// console.log("[EXPORT LUT] ACES input + blocks");

				newPasses.splice(newPasses.length - config.endFixedPasses); // splicing end fixed passes
				gpu.composer.passes = newPasses;
				gpu.render();

				blocksData = blocksData.splice(1); // render block
				blocksData.splice(blocksData.length - config.endFixedPasses); // ODT block
			} else if (!data.options.ACESinput && !data.options.ACESoutput) {
				// console.log("[EXPORT LUT] blocks");

				if (lutify.fixBlueLightArtifacts) newPasses[2].enabled = true;

				let renderPass = newPasses[0]; // extracting render pass
				newPasses = newPasses.splice(config.startFixedPasses); // splicing start fixed passes (also render pass)
				newPasses.splice(0, 0, renderPass); // adding the renderPass to beginning
				newPasses.splice(newPasses.length - config.endFixedPasses); //splicing end fixed passes
				gpu.composer.passes = newPasses;
				gpu.render();

				blocksData = blocksData.splice(config.startFixedPasses); // render + IDT block
				blocksData.splice(blocksData.length - config.endFixedPasses); // ODT block
			}
		} else if (exportType == "vlt") {
			gpu.composer.passes = newPasses;
			gpu.composer.passes[2].enabled = false; // fix blue light pass

			const selectedIDT = luts.IDT.find(idt => idt.value == "IDT109");
			gpu.composer.passes[1] = this.createACESpass("IDT", { ...selectedIDT, ACES_LMT: lutify.fixBlueLightArtifacts }); // swapping IDT pass
			if (!lutify.fixBlueLightArtifacts) gpu.composer.passes[3].enabled = true;

			const selectedODT = luts.ODT.find(odt => odt.value == "ODT404");
			gpu.composer.passes[gpu.composer.passes.length - 1] = this.createACESpass("ODT", selectedODT); // swapping ODT pass
			gpu.render();

			blocksData = blocksData.splice(1); // render block
		} else if (exportType == "IPP2") {
			gpu.composer.passes = newPasses;
			gpu.composer.passes[2].enabled = false; // fix blue light pass

			let selectedIDT = luts.IDT.find(idt => idt.value == "IDT110");
			gpu.composer.passes[1] = this.createACESpass("IDT", { ...selectedIDT, ACES_LMT: lutify.fixBlueLightArtifacts }); // swapping IDT pass
			if (!lutify.fixBlueLightArtifacts) gpu.composer.passes[3].enabled = true; // gamut compress pass
			gpu.composer.passes[gpu.composer.passes.length - 1] = this.createLUTPass(luts.IPP2[0].texture, true); // swapping ODT pass
			gpu.render();

			blocksData = blocksData.splice(1); // render block
		} else if (exportType == "davinci") {
			gpu.composer.passes = newPasses;
			gpu.composer.passes[1] = this.createLUTPass(luts.davinci[0].texture); // swapping IDT pass
			if (lutify.fixBlueLightArtifacts) gpu.composer.passes[2].enabled = true; // FBL pass
			else gpu.composer.passes[3].enabled = true; // gamut compress pass
			gpu.composer.passes[gpu.composer.passes.length - 1] = this.createLUTPass(luts.davinci[1].texture, true); // swapping ODT pass
			gpu.render();
		} else if (exportType == "logc4") {
			gpu.composer.passes = newPasses;
			gpu.composer.passes[1] = this.createLUTPass(luts.logc4[0].texture); // swapping IDT pass
			if (lutify.fixBlueLightArtifacts) gpu.composer.passes[2].enabled = true; // FBL pass
			else gpu.composer.passes[3].enabled = true; // gamut compress pass
			gpu.composer.passes[gpu.composer.passes.length - 1] = this.createLUTPass(luts.logc4[1].texture, true); // swapping ODT pass
			gpu.render();
		} else if (exportType == 'CO_step1') {
			gpu.composer.passes = newPasses;
			gpu.composer.passes[2].enabled = false; // fix blue light pass

			const IDT = luts.IDT.find(idt => idt.value == "LIDT110");
			gpu.composer.passes[1] = this.createIDTPass(IDT); // swapping IDT pass
			if (!lutify.fixBlueLightArtifacts) gpu.composer.passes[3].enabled = true;

			const ODT = luts.ODT.find(odt => odt.value == "ODT410");
			gpu.composer.passes[gpu.composer.passes.length - 1] = this.createACESpass("ODT", ODT); // swapping ODT pass
			gpu.render();

			blocksData = blocksData.splice(1); // render block
		} else if (exportType == 'CO_step2') {
			gpu.composer.passes = [];
			gpu.composer.passes.push(newPasses[0]);

			const cameraLut = luts.CO.find(lut => lut.title == `Capture One/${data.options.camera.lut}`);
			gpu.composer.passes.push(this.createLUTPass(cameraLut.texture));

			gpu.composer.passes.push(this.createLUTPass(luts.COStep1Result[0].texture));

			gpu.render();
		}

		// Reading pixels from the buffer
		let pixels = new Float32Array(w * h * 4);

		const copyPass = new CopyPass();
		gpu.composer.addPass(copyPass);
		copyPass.renderToScreen = false // important because otherwise the renderTarget is set to null

		gpu.render();
		gpu.renderer.readRenderTargetPixels(gpu.composer.renderer.getRenderTarget(), 0, 0, w, h, pixels);

		// Now we are restoring the scene.children[1] with the original image texture and re-rendering it to the canvas
		gpu.renderer.setPixelRatio(1);
		gpu.renderer.setSize(lutify.imageWidth, lutify.imageHeight, false);
		gpu.composer.setSize(tw, th, false);

		w = gpu.scene.children[0].geometry.parameters.width;
		h = gpu.scene.children[0].geometry.parameters.height;

		gpu.camera.zoom = cameraZoom;
		gpu.camera.position.x = cameraPositionX;
		gpu.camera.position.y = cameraPositionY;

		gpu.camera.left = w / -2;
		gpu.camera.right = w / 2;
		gpu.camera.top = h / 2;
		gpu.camera.bottom = h / -2;
		gpu.camera.updateProjectionMatrix();

		gpu.scene.children[0].scale.x = gpu.scene.children[0].scale.y = 1;
		gpu.scene.children[0].material.map = origTexture;

		originalPasses[2].enabled = isFixBlueLightPassEnabled;
		originalPasses[3].enabled = isGamutCompressPassEnabled;
		gpu.composer.passes = originalPasses;
		gpu.composer.passes[gpu.composer.passes.length - 1].renderToScreen = true;
		gpu.render();

		return { pixels, blocksData };
	}

	isRunningOnM1() {
		let canvas = document.createElement('canvas');
		let gl;
		var debugInfo;
		var renderer;

		try {
			gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
		} catch (e) {
			console.log(e)
		}

		if (gl) {
			debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
			renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
			if (renderer.indexOf('M1') == -1) return true;
		}
	}
}
